/* eslint-disable @typescript-eslint/camelcase */
import { Component, Vue } from 'vue-property-decorator'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import AppForm from '@core/components/AppForm.vue'
import AppDatatable from '@core/components/AppDatatable.vue'
import { IDateService } from '@master/services/IDateService'
import { ClientScreening, DDTypeOptions, OutcomeOptions, RiskAssessmentOptions, ScreeningTypeOptions } from '@core/pages/client-management/model/ClientScreening'
import { FormStructure } from '@master/model/structure/FormStructure'
import { ClientProfile } from '../model/ClientProfile'
import { namespace } from 'vuex-class'
import { ClientPolicyMeta } from '@core/pages/new-business-register/model/ClientPolicyMeta'
import AppDialog from '@core/components/AppDialog.vue'

const clientInfoStore = namespace('ClientInfoStore')

@Component({
  components: {
    AppForm,
    AppDatatable,
    AppDialog
  }
})
export class ScreeningsMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @inject()
  protected $api: API

  @clientInfoStore.State
  private clientProfile: ClientProfile

  @clientInfoStore.State
  private readOnly

  protected screenings: ClientScreening[] = []
  protected newScreening = new ClientScreening()
  protected showScreeningForm = false
  private clientPolicies: ClientPolicyMeta[] = []

  formattedDate (unixTime: number | string) {
    return this.dateService.format(Number(unixTime), 'YYYY-MM-DD')
  }

  get curUser () {
    return this.$store.getters.currentUser
  }

  get isSuperUser () {
    return this.curUser?.isSuperUser || false
  }

  get policyOid () {
    return this.$route.params.policyOid
  }

  get clientOid () {
    return this.$route.params.clientOid
  }

  get columns (): {label: string; field: keyof ClientScreening}[] {
    return [
      { label: 'Type of Screening', field: 'type' },
      { label: 'Date Screened', field: 'screenDateDisplay' },
      { label: 'Performed By', field: 'performedBy' },
      { label: 'Outcome', field: 'outcome' },
      { label: 'Risk Assessment', field: 'riskAssessment' },
      { label: 'DD Type Conducted', field: 'ddTypeConducted' },
      { label: 'Remarks', field: 'remarks' }
    ]
  }

  get policyColumns () {
    return [
      { label: 'New Business ID', field: 'businessId' },
      { label: 'High Risk', field: 'clientMetadata.highRisk' },
      { label: 'Performed By', field: 'clientMetadata.amlScreeningDoneBy' },
      { label: 'Resolution Status', field: 'clientMetadata.resolutionStatus' },
      { label: 'Risk Status', field: 'clientMetadata.riskStatus' },
      { label: 'Reason', field: 'clientMetadata.reason' },
      { label: 'Notes', field: 'clientMetadata.notes' }
    ]
  }

  get screeningStructure (): FormStructure<ClientScreening> {
    return [
      {
        label: 'Type of Screening',
        key: 'type',
        options: ScreeningTypeOptions
      },
      {
        label: 'Date Screened',
        key: 'screeningDate',
        type: 'date'
      },
      {
        label: 'Performed By',
        key: 'performedBy'
      },
      {
        label: 'Outcome',
        key: 'outcome',
        options: OutcomeOptions
      },
      {
        label: 'Risk Assessment',
        key: 'riskAssessment',
        options: RiskAssessmentOptions
      },
      {
        label: 'DD Type Conducted',
        key: 'ddTypeConducted',
        options: DDTypeOptions
      },
      {
        label: 'Remarks',
        key: 'remarks',
        type: 'textarea',
        classes: ['colspan']
      }
    ]
  }

  get filteredScreening () {
    const newScreens = []
    const oldScreens = []
    this.screenings.forEach((screen) => {
      const sameType = newScreens.find(ns => ns.type === screen.type)
      if (!sameType) {
        newScreens.push(screen)
        return
      }
      const isLatestIncluded = sameType.screeningDate >= screen.screeningDate
      if (!isLatestIncluded) {
        oldScreens.push(sameType)
        const curIdx = newScreens.findIndex(ns => ns.type === screen.type)
        newScreens.splice(curIdx, 1, screen)
      }
    })
    return { newScreens, oldScreens }
  }

  createScreening () {
    this.newScreening = new ClientScreening()
    this.showScreeningForm = true
  }

  async fetchScreeningData () {
    const response = await this.$api.clientScreenings.get({
      client_oid: this.clientOid
    })
    response.data.forEach((cs) => {
      const newScreening = new ClientScreening()
      newScreening.extractAndAssignData(cs)
      this.screenings.push(newScreening)
    })
  }

  async fetchClientPolicies () {
    const queryHeaders = [
      '_oid',
      'client_oid',
      'business_id',
      'owners_name',
      'owners_nric',
      'principal',
      'name',
      'application_date',
      'ops_approval_date',
      'status',
      'type',
      'policy_number',
      'creation_type',
      'client_metadata'
    ]

    const res: { data: Array<any[]> } = await this.$api.clientPolicy.list({
      wildcardMode: 'suffix,prefix',
      orderBy: 'application_date',
      headers: JSON.stringify(queryHeaders),
      query: 'client_oid = ? AND status != ?',
      queryArgs: JSON.stringify([this.clientOid, 'deleted'])
    })

    this.clientPolicies = res.data.map(policyMeta => {
      const policy = new ClientPolicyMeta()
      policy.assignData(queryHeaders, policyMeta)
      return policy
    })
  }

  async save () {
    const rawNewScreening = this.newScreening.convertToRawData()

    if (Object.values(rawNewScreening).some(e => !e)) {
      alert('Please fill up all fields!')
      return
    }

    await this.$api.clientScreenings.new({
      client_oid: this.clientOid,
      data: rawNewScreening
    })
    this.showScreeningForm = false
    const updated = new ClientScreening()
    updated.extractAndAssignData(rawNewScreening)
    this.screenings.push(updated)
  }

  async created () {
    await this.fetchScreeningData()
    await this.fetchClientPolicies()
  }
}
